"use client"

import { type ReactNode, useState } from "react"

import Image from "next/image"
import { useTranslations } from "next-intl"

import { LISTING_CONTROL_CLASS } from "@/components/marketplace/listing/listing-control-styles"
import { CheckIcon, ChevronIcon, Menu, Pill, RadioCards, Sheet, SortIcon, SparkleIcon, WalletIcon } from "@/components/ui"
import { itemAt } from "@/lib/helpers/array"
import { getSortOptions } from "@/lib/types/search"
import type { SearchSort } from "@/lib/types/search"
import { trackSelectContent } from "@/platform/observability/analytics/v2/search"

interface SortControlProps {
  value: SearchSort
  onChange: (sort: SearchSort) => void
  compact?: boolean
  chip?: boolean
}

const SORT_ICON: Record<SearchSort, ReactNode> = {
  relevance: <SparkleIcon size={18} />,
  price_asc: <WalletIcon size={18} />,
  price_desc: <WalletIcon size={18} />,
}

export const SortControl = ({ value, onChange, compact = false, chip = false }: SortControlProps) => {
  const t = useTranslations("search")
  const sortOptions = getSortOptions(t)
  const [open, setOpen] = useState(false)
  const current = sortOptions.find((option) => option.value === value) ?? itemAt(sortOptions, 0)
  const chipTrigger = (onClick?: () => void) => (
    <Pill tone="outline" size="sm" aria-label={t("sort.trigger", { label: current.label })} className={LISTING_CONTROL_CLASS} onClick={onClick}>
      {t("sort.chip")}
      <span className="ml-125 flex h-1.5 w-2.75 shrink-0 items-center justify-center">
        <Image src="/images/icons/listing-chevron.svg" alt="" width={7.07781} height={11.7372} className="shrink-0 rotate-90" />
      </span>
    </Pill>
  )
  const reportOpened = (opened: boolean) => {
    if (opened) trackSelectContent({ content_type: "sort_menu", content_id: "open" })
  }
  const openSheet = () => {
    reportOpened(true)
    setOpen(true)
  }

  const selectSort = (sort: SearchSort) => {
    onChange(sort)
    setOpen(false)
  }

  return (
    <>
      <div className="@3xl:hidden">
        {chip ? (
          chipTrigger(openSheet)
        ) : compact ? (
          <Pill tone="outline" size="sm" aria-label={t("sort.trigger", { label: current.label })} className="h-11 px-400" onClick={openSheet}>
            <SortIcon size={16} />
            {current.label}
            <ChevronIcon size={14} className="rotate-90" />
          </Pill>
        ) : (
          <Pill tone="outline" size="sm" className="h-11" onClick={openSheet}>
            <SortIcon size={14} />
            {t("sort.trigger", { label: current.label })}
            <ChevronIcon size={14} className="rotate-90" />
          </Pill>
        )}
        <Sheet open={open} onClose={setOpen} solid compact handle title={t("sort.title")}>
          <RadioCards
            compact
            label={t("sort.title")}
            value={value}
            options={sortOptions.map((option) => ({ value: option.value, label: t(`sort.mobileLabels.${option.value}`) }))}
            onChange={selectSort}
            onReselect={selectSort}
          />
        </Sheet>
      </div>

      <div className="hidden @3xl:block">
        <Menu
          align="end"
          onOpenChange={reportOpened}
          trigger={
            chip ? (
              chipTrigger()
            ) : (
              <Pill tone="outline" size="sm">
                <SortIcon size={14} />
                {t("sort.trigger", { label: current.label })}
                <ChevronIcon size={14} className="rotate-90" />
              </Pill>
            )
          }
          items={sortOptions.map((option) => ({
            label: option.label,
            icon: option.value === value ? <CheckIcon size={16} /> : SORT_ICON[option.value],
            onSelect: () => onChange(option.value),
          }))}
        />
      </div>
    </>
  )
}
